/** Programma di audit (AUD-F2.14): modello da scaricare, programma compilato caricato come evidenza, invio al fornitore. */
import { useState } from "react";
import { Alert, Button, Card, DatePicker, Input, Popconfirm, Segmented, Space, Steps, Tag, Typography, Upload } from "antd";
import { DownloadOutlined, FileWordOutlined, UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import type { AuditDetailDto } from "@audit-plan/shared";
import { invalidateAudit } from "../../api/audits";
import { evidenceDownloadUrl, uploadEvidence } from "../../api/evidence";
import { programmeTemplateUrl, setProgramme, setProgrammeSent } from "../../api/programme";
import { useNotify } from "./useNotify";
import { fmtD } from "../pills";
import { ProgrammeDocPanel } from "./ProgrammeDocPanel";

type Phase = "todo" | "ready" | "sent";
export const programmePhase = (a: AuditDetailDto): Phase => (a.programme.sentAt ? "sent" : a.programme.evidenceId ? "ready" : "todo");
const phaseColor: Record<Phase, string> = { todo: "default", ready: "gold", sent: "green" };

export function ProgrammeTag({ audit }: { audit: AuditDetailDto }) {
  const { t } = useTranslation();
  const p = programmePhase(audit);
  return <Tag color={phaseColor[p]} style={{ marginInlineStart: 6 }}>{t(`pg_${p}`)}</Tag>;
}

/** Flusso Word di AUD-F2.14 (scarica il modello, carica il compilato, registra l'invio): resta come alternativa. */
function ProgrammeWordFlow({ audit: a }: { audit: AuditDetailDto }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const n = useNotify();
  const [busy, setBusy] = useState(false);
  const [sentAt, setSentAt] = useState<Dayjs | null>(dayjs());
  const [sentTo, setSentTo] = useState("");
  const can = a.allowed.programme;
  const ev = a.evidence.find((e) => e.id === a.programme.evidenceId) ?? null;
  const lastV = ev?.versions.at(-1)?.v ?? 1;
  const phase = programmePhase(a);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      invalidateAudit(qc, a.id);
      n.ok();
    } catch (e) {
      n.fail(e);
    } finally {
      setBusy(false);
    }
  };
  const upload = (file: File) => {
    void run(async () => {
      const e = await uploadEvidence(file, { supplierId: a.supplierId, auditId: a.id, target: { type: "audit", id: a.id } }, t("pg_evidenceDesc"));
      await setProgramme(a.id, e.id);
    });
    return false;
  };

  return (
    <Card>
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t("pg_intro")} />
      <Steps
        direction="vertical"
        current={phase === "todo" ? 1 : phase === "ready" ? 2 : 3}
        items={[
          {
            title: t("pg_step1"),
            description: (
              <Space direction="vertical" size={6} style={{ marginBottom: 12 }}>
                <Typography.Text type="secondary">{t("pg_step1Hint")}</Typography.Text>
                <Button icon={<DownloadOutlined />} href={programmeTemplateUrl()}>{t("pg_downloadTemplate")}</Button>
              </Space>
            ),
          },
          {
            title: t("pg_step2"),
            description: (
              <Space direction="vertical" size={6} style={{ marginBottom: 12 }}>
                {ev ? (
                  <Space wrap>
                    <FileWordOutlined />
                    <a href={evidenceDownloadUrl(ev.id, lastV)}>{ev.name}</a>
                    {can && (
                      <Popconfirm title={t("pg_removeQ")} onConfirm={() => void run(() => setProgramme(a.id, null))} okText={t("confirm")} cancelText={t("cancel")}>
                        <Button size="small" type="link" danger disabled={busy}>{t("pg_remove")}</Button>
                      </Popconfirm>
                    )}
                  </Space>
                ) : (
                  <Typography.Text type="secondary">{t("pg_noFile")}</Typography.Text>
                )}
                {can && (
                  <Upload accept=".docx,.pdf" showUploadList={false} beforeUpload={upload} disabled={busy}>
                    <Button icon={<UploadOutlined />} loading={busy}>{ev ? t("pg_replace") : t("pg_upload")}</Button>
                  </Upload>
                )}
              </Space>
            ),
          },
          {
            title: t("pg_step3"),
            description: a.programme.sentAt ? (
              <Space wrap>
                <span data-sensitive>{t("pg_sentOn", { d: fmtD(a.programme.sentAt), to: a.programme.sentTo })}</span>
                {can && (
                  <Popconfirm title={t("pg_unsendQ")} onConfirm={() => void run(() => setProgrammeSent(a.id, null))} okText={t("confirm")} cancelText={t("cancel")}>
                    <Button size="small" type="link" disabled={busy}>{t("pg_unsend")}</Button>
                  </Popconfirm>
                )}
              </Space>
            ) : can ? (
              <Space wrap>
                <DatePicker value={sentAt} onChange={setSentAt} format="DD/MM/YYYY" allowClear={false} />
                <Input data-sensitive style={{ width: 280 }} value={sentTo} onChange={(e) => setSentTo(e.target.value)} placeholder={t("pg_sentToPh")} />
                <Button type="primary" disabled={!ev || !sentAt || !sentTo.trim() || busy} onClick={() => void run(() => setProgrammeSent(a.id, sentAt!.format("YYYY-MM-DD"), sentTo.trim()))}>
                  {t("pg_markSent")}
                </Button>
              </Space>
            ) : (
              <Typography.Text type="secondary">{t("pg_notSent")}</Typography.Text>
            ),
          },
        ]}
      />
    </Card>
  );
}

/** Scheda «Programma» dell'audit: compilazione nel tool (AUD-F2.14b, predefinita) oppure il flusso Word. */
export function ProgrammeTab({ audit }: { audit: AuditDetailDto }) {
  const { t } = useTranslation();
  // se il programma in uso è un Word caricato a mano si apre sul flusso Word
  const manual = !!audit.programme.evidenceId && !audit.evidence.find((e) => e.id === audit.programme.evidenceId)?.description?.startsWith("Programma di audit (generato dal tool)");
  const [mode, setMode] = useState<"tool" | "word">(manual ? "word" : "tool");
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <Segmented value={mode} onChange={(v) => setMode(v as "tool" | "word")} options={[{ value: "tool", label: t("pd_modeTool") }, { value: "word", label: t("pd_modeWord") }]} style={{ justifySelf: "start" }} />
      {mode === "tool" ? <ProgrammeDocPanel audit={audit} /> : <ProgrammeWordFlow audit={audit} />}
    </div>
  );
}
